import { useState } from 'react';
import { IBlogPageViewModel } from 'Types/Litium';
import CloseIcon from 'Icons/close.svg?react';
import MagnicifactionGlassIcon from 'Icons/magnificationGlass.svg?react';

interface BlogMobileMenuProps {
    expandBlogListIconAriaLabel: IBlogPageViewModel['expandBlogListIconAriaLabel'];
    blogMobileNavigationLinkText: IBlogPageViewModel['blogMobileNavigationLinkText'];
    filterTagListHeaderText: IBlogPageViewModel['filterTagListHeaderText'];
    searchLinkBase: IBlogPageViewModel['searchLinkBase'];
    missionAndVisionPageLink: IBlogPageViewModel['missionAndVisionPageLink'];
    thisWeeksAuthorsThumbnail: IBlogPageViewModel['thisWeeksAuthorsThumbnail'];
    navigationTopLevelLinkList: IBlogPageViewModel['navigationTopLevelLinkList'];
    navigationBlogItemLinkList: IBlogPageViewModel['navigationBlogItemLinkList'];
    blogHomeLink: IBlogPageViewModel['blogHomeLink'];
    navigationBlogItemListHeaderText: IBlogPageViewModel['navigationBlogItemListHeaderText'];
    archiveActionLink: IBlogPageViewModel['archiveActionLink'];
    allAuthorsActionLink: IBlogPageViewModel['allAuthorsActionLink'];
    thisWeeksAuthorHeaderText: IBlogPageViewModel['thisWeeksAuthorHeaderText'];
    blogSearchPlaceholderText: IBlogPageViewModel['blogSearchPlaceholderText'];
    filterTagList: IBlogPageViewModel['filterTagList'];
}

const BlogMobileMenu = ({
    expandBlogListIconAriaLabel,
    blogMobileNavigationLinkText,
    filterTagListHeaderText,
    searchLinkBase,
    missionAndVisionPageLink,
    thisWeeksAuthorsThumbnail,
    navigationTopLevelLinkList,
    navigationBlogItemLinkList,
    blogHomeLink,
    navigationBlogItemListHeaderText,
    archiveActionLink,
    allAuthorsActionLink,
    thisWeeksAuthorHeaderText,
    blogSearchPlaceholderText,
    filterTagList,
}: BlogMobileMenuProps) => {
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [expandMobileMenuPostList, setExpandMobileMenuPostList] =
        useState(false);
    const [expandMobileMenuFilterTagList, setExpandMobileMenuFilterTagList] =
        useState(false);

    return (
        blogMobileNavigationLinkText && (
            <div className="relative">
                <button
                    className="px-2.5 pb-3 font-montserrat font-medium underline lg:hidden"
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                    {blogMobileNavigationLinkText}
                </button>

                <ul
                    className={`absolute top-0 z-[1] max-w-sm bg-white p-3.5 transition-all duration-layout ease-in-out lg:hidden ${
                        showMobileMenu
                            ? 'left-0 grid-rows-[1fr]'
                            : '-left-full grid-rows-[0fr]'
                    } ${expandMobileMenuPostList ? 'max-h-3xl' : 'max-h-xl'}`}
                >
                    <li className="flex">
                        <form
                            className="relative flex h-10 w-full items-center gap-2 text-xs"
                            method="get"
                            action={searchLinkBase}
                        >
                            <label
                                className="absolute left-0"
                                htmlFor="quicksearch"
                                id="quicksearchLabel"
                            >
                                <MagnicifactionGlassIcon
                                    title={blogSearchPlaceholderText}
                                    className="h-6 w-6"
                                />
                                <span className="sr-only">
                                    {blogSearchPlaceholderText}
                                </span>
                            </label>
                            <input
                                id="query"
                                type="text"
                                name="query"
                                aria-labelledby="quicksearchLabel"
                                className="mr-2 h-full w-full indent-7 font-roboto text-base font-normal text-black placeholder:font-roboto placeholder:font-normal placeholder:text-black"
                                placeholder={blogSearchPlaceholderText}
                            />
                        </form>

                        <CloseIcon
                            title="Close"
                            className="top-2 float-right h-5 w-5 cursor-pointer text-black"
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                        />
                    </li>

                    <li className="mb-5">
                        <ul>
                            <li>
                                <a
                                    className="font-roboto text-base font-normal uppercase"
                                    href={blogHomeLink.href}
                                >
                                    {blogHomeLink.text}
                                </a>
                            </li>

                            {missionAndVisionPageLink && (
                                <li className="m-2.5">
                                    <a
                                        className="font-roboto text-base font-normal"
                                        href={missionAndVisionPageLink.href}
                                    >
                                        {missionAndVisionPageLink.text}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </li>

                    {navigationTopLevelLinkList.length > 0 && (
                        <li className="mb-5">
                            <ul className="mt-2">
                                {navigationTopLevelLinkList.map(
                                    (item, index) => (
                                        <li key={index}>
                                            <a
                                                className="font-roboto text-base font-normal uppercase"
                                                href={item.href}
                                            >
                                                {item.text}
                                            </a>
                                        </li>
                                    )
                                )}
                            </ul>
                        </li>
                    )}

                    {thisWeeksAuthorsThumbnail && (
                        <li className="mb-5">
                            <a
                                className="font-roboto text-base font-normal uppercase"
                                href={thisWeeksAuthorsThumbnail.url}
                            >
                                {thisWeeksAuthorHeaderText}
                            </a>
                        </li>
                    )}

                    <li className="mb-5">
                        <a
                            className="font-roboto text-base font-normal uppercase"
                            href={allAuthorsActionLink.href}
                        >
                            {allAuthorsActionLink.text}
                        </a>
                    </li>

                    <li
                        className={`${filterTagList?.length > 0 ? 'mb-5' : ''}`}
                    >
                        <button
                            aria-label={expandBlogListIconAriaLabel}
                            className="text-left font-roboto text-base font-medium uppercase"
                            onClick={() =>
                                setExpandMobileMenuPostList(
                                    !expandMobileMenuPostList
                                )
                            }
                        >
                            {navigationBlogItemListHeaderText}
                        </button>

                        <button
                            aria-label={expandBlogListIconAriaLabel}
                            className="relative float-right flex h-[31px] w-[31px] items-center justify-center"
                            onClick={() =>
                                setExpandMobileMenuPostList(
                                    !expandMobileMenuPostList
                                )
                            }
                        >
                            <span
                                className={`absolute h-6 w-px bg-black transition-all duration-layout ease-in-out ${
                                    expandMobileMenuPostList ? 'rotate-90' : ''
                                }`}
                            />
                            <span className="absolute h-px w-6 bg-black transition-all duration-layout ease-in-out" />
                        </button>

                        <div
                            className={`grid overflow-hidden transition-all duration-layout ${
                                expandMobileMenuPostList
                                    ? 'visible grid-rows-[1fr]'
                                    : 'grid-rows-[0fr]'
                            }`}
                        >
                            {navigationBlogItemLinkList && (
                                <ul className="overflow-hidden">
                                    {navigationBlogItemLinkList.length > 0 && (
                                        <li className="mb-5">
                                            <ul>
                                                {navigationBlogItemLinkList?.map(
                                                    (item, index) => (
                                                        <li
                                                            className="mb-6 ml-6 mr-6 mt-5"
                                                            key={index}
                                                        >
                                                            <a
                                                                className="truncate font-roboto text-base font-normal"
                                                                href={item.href}
                                                            >
                                                                {item.text}
                                                            </a>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </li>
                                    )}

                                    <li className="ml-6">
                                        <a
                                            className="font-roboto text-base font-normal uppercase"
                                            href={archiveActionLink.href}
                                        >
                                            {archiveActionLink.text}
                                        </a>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </li>

                    {filterTagList && filterTagList.length > 0 && (
                        <li>
                            <button
                                aria-label={expandBlogListIconAriaLabel}
                                className="text-left font-roboto text-base font-medium uppercase"
                                onClick={() =>
                                    setExpandMobileMenuFilterTagList(
                                        !expandMobileMenuFilterTagList
                                    )
                                }
                            >
                                {filterTagListHeaderText}
                            </button>

                            <button
                                aria-label={expandBlogListIconAriaLabel}
                                className="relative float-right flex h-[31px] w-[31px] items-center justify-center"
                                onClick={() =>
                                    setExpandMobileMenuFilterTagList(
                                        !expandMobileMenuFilterTagList
                                    )
                                }
                            >
                                <span
                                    className={`absolute h-6 w-px bg-black transition-all duration-layout ease-in-out ${
                                        expandMobileMenuFilterTagList
                                            ? 'rotate-90'
                                            : ''
                                    }`}
                                />
                                <span className="absolute h-px w-6 bg-black transition-all duration-layout ease-in-out" />
                            </button>

                            <div
                                className={`grid overflow-hidden transition-all duration-layout ${
                                    expandMobileMenuFilterTagList
                                        ? 'visible grid-rows-[1fr]'
                                        : 'grid-rows-[0fr]'
                                }`}
                            >
                                <ul className="overflow-hidden">
                                    {filterTagList.length > 0 && (
                                        <li className="mb-5">
                                            <ul>
                                                {filterTagList.map(
                                                    (item, index) => (
                                                        <li
                                                            className="mb-6 ml-6 mr-6 mt-5"
                                                            key={index}
                                                        >
                                                            <a
                                                                className="truncate font-roboto text-base font-normal"
                                                                href={item.url}
                                                            >
                                                                {item.name}
                                                            </a>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        )
    );
};

export default BlogMobileMenu;
